'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  serviceType: 'maid' | 'apartment' | 'hospitality';
  serviceName: string;
  serviceRate: number;
  serviceRateUnit: string;
  serviceImage: string;
  serviceImageAlt: string;
}

interface BookingFormData {
  startDate: string;
  endDate: string;
  startTime: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  paymentMethod: string;
}

const steps = ['Service Details', 'Your Information', 'Confirm & Pay'];

export default function BookingModal({
  open,
  onClose,
  serviceType,
  serviceName,
  serviceRate,
  serviceRateUnit,
  serviceImage,
  serviceImageAlt,
}: BookingModalProps) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<BookingFormData>({
    defaultValues: {
      paymentMethod: 'card',
    }
  });

  const watchedData = watch();

  const handleNext = () => {
    if (step < 2) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    // TODO: POST /api/bookings with form data and serviceId
    await new Promise((r) => setTimeout(r, 2000));
    setSubmitting(false);
    setConfirmed(true);
    toast.success(`Booking confirmed for ${serviceName}! Check your email for details.`);
  };

  const handleClose = () => {
    setStep(0);
    setConfirmed(false);
    onClose();
  };

  if (confirmed) {
    return (
      <Modal open={open} onClose={handleClose} title="Booking Confirmed!" size="md">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={36} className="text-emerald-600" />
          </div>
          <h3 className="font-display font-bold text-xl text-foreground mb-2">Booking Confirmed!</h3>
          <p className="text-muted-foreground text-sm mb-1">
            Your booking for <span className="font-semibold text-foreground">{serviceName}</span> has been confirmed.
          </p>
          <p className="text-muted-foreground text-sm mb-6">
            Confirmation details have been sent to your email address.
          </p>
          <div className="bg-muted rounded-xl p-4 mb-6 text-left">
            <p className="text-xs text-muted-foreground mb-1">Booking Reference</p>
            <p className="font-display font-bold text-lg text-primary tabular-nums">
              BKG-{Math.floor(10000 + (serviceRate * 3) % 90000)}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Book ${serviceName}`} size="lg">
      <div className="p-6">
        {/* Step indicator */}
        <div className="flex items-center gap-0 mb-8">
          {steps.map((s, i) => (
            <React.Fragment key={`step-indicator-${i}`}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i < step
                      ? 'bg-primary text-white'
                      : i === step
                      ? 'bg-primary text-white ring-4 ring-primary/20' :'bg-muted text-muted-foreground'
                  }`}
                >
                  {i < step ? <Icon name="CheckIcon" size={14} /> : i + 1}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:block ${
                    i === step ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 transition-colors ${
                    i < step ? 'bg-primary' : 'bg-border'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Service Details */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="bg-muted rounded-xl p-4 flex items-center gap-4">
              {serviceImage && (
                <img src={serviceImage} alt={serviceImageAlt} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
              )}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">{serviceType}</p>
                <p className="font-display font-semibold text-foreground">{serviceName}</p>
                <p className="text-primary font-bold text-lg tabular-nums">
                  ${serviceRate.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">{serviceRateUnit}</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Start Date <span className="text-destructive">*</span>
                </label>
                <input
                  {...register('startDate', { required: 'Start date is required' })}
                  type="date"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                    errors.startDate ? 'border-destructive' : 'border-border focus:border-primary'
                  }`}
                />
                {errors.startDate && <p className="text-destructive text-xs mt-1">{errors.startDate.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {serviceType === 'apartment' ? 'End Date' : 'Preferred Time'} <span className="text-destructive">*</span>
                </label>
                {serviceType === 'apartment' ? (
                  <input
                    {...register('endDate', { required: 'End date is required' })}
                    type="date"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                      errors.endDate ? 'border-destructive' : 'border-border focus:border-primary'
                    }`}
                  />
                ) : (
                  <input
                    {...register('startTime', { required: 'Time is required' })}
                    type="time"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                      errors.startTime ? 'border-destructive' : 'border-border focus:border-primary'
                    }`}
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Special Requests</label>
              <p className="text-xs text-muted-foreground mb-1">Any specific requirements or preferences</p>
              <textarea
                {...register('specialRequests')}
                rows={3}
                placeholder="e.g. Pet-friendly products, specific time preferences, accessibility needs..."
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 1: Personal Info */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  First Name <span className="text-destructive">*</span>
                </label>
                <input
                  {...register('firstName', { required: 'First name is required' })}
                  type="text"
                  placeholder="First name"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                    errors.firstName ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
                  }`}
                />
                {errors.firstName && <p className="text-destructive text-xs mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Last Name <span className="text-destructive">*</span>
                </label>
                <input
                  {...register('lastName', { required: 'Last name is required' })}
                  type="text"
                  placeholder="Last name"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                    errors.lastName ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
                  }`}
                />
                {errors.lastName && <p className="text-destructive text-xs mt-1">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Address <span className="text-destructive">*</span>
              </label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' }
                })}
                type="email"
                placeholder="your@email.com"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                  errors.email ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
                }`}
              />
              {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Phone Number <span className="text-destructive">*</span>
              </label>
              <input
                {...register('phone', { required: 'Phone number is required' })}
                type="tel"
                placeholder="+1 (555) 000-0000"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
                  errors.phone ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
                }`}
              />
              {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Payment Method</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'card', label: 'Credit Card', icon: 'CreditCardIcon' },
                  { value: 'bank', label: 'Bank Transfer', icon: 'BuildingLibraryIcon' },
                  { value: 'cash', label: 'Cash on Day', icon: 'BanknotesIcon' },
                ].map((pm) => (
                  <label
                    key={`pm-${pm.value}`}
                    className={`flex flex-col items-center gap-1.5 p-3 border rounded-xl cursor-pointer transition-all ${
                      watchedData.paymentMethod === pm.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      {...register('paymentMethod')}
                      type="radio"
                      value={pm.value}
                      className="sr-only"
                    />
                    <Icon name={pm.icon as 'CreditCardIcon'} size={20} className={watchedData.paymentMethod === pm.value ? 'text-primary' : 'text-muted-foreground'} />
                    <span className="text-xs font-medium text-center">{pm.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <h4 className="font-display font-semibold text-sm text-foreground mb-3">Booking Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium text-foreground">{serviceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium text-foreground">{watchedData.startDate || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium text-foreground">{watchedData.firstName} {watchedData.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium text-foreground">{watchedData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment</span>
                  <span className="font-medium text-foreground capitalize">{watchedData.paymentMethod}</span>
                </div>
                <div className="border-t border-primary/20 pt-2 flex justify-between">
                  <span className="font-semibold text-foreground">Rate</span>
                  <span className="font-bold text-primary tabular-nums">${serviceRate.toLocaleString()}{serviceRateUnit}</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              By confirming, you agree to our booking terms. A confirmation email will be sent to your address. Cancellation is free up to 24 hours before the booking.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mt-8 pt-4 border-t border-border">
          <button
            onClick={step === 0 ? handleClose : handleBack}
            className="px-4 py-2.5 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          {step < 2 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2.5 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={submitting}
              className="px-6 py-2.5 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95 disabled:opacity-70 flex items-center gap-2"
              style={{ minWidth: '140px' }}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Confirming...
                </>
              ) : (
                'Confirm Booking'
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}